// src/services/importOrderService.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  Timestamp,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
  runTransaction,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../firebase/config';
import {
  ImportOrder,
  ImportOrderFormData,
  ImportOrderStatus,
  PaymentStatus,
  ImportOrderStats,
  Supplier,
  ImportOrderItem,
  ImportOrderDocument,
  ImportOrderDocumentType,
  LcStatus,
  ImportOrderCost,
  ImportOrderStatusHistoryEntry,
  ImportOrderPayment,
  sumCosts,
} from './ImportOrder';
import { ProductService } from '../car/carService';

const IMPORT_ORDERS_COLLECTION = 'importOrders';
const SUPPLIER_COLLECTION = 'suppliers';

export interface PaginatedImportOrdersResult {
  orders: ImportOrder[];
  totalCount: number;
  // Kept for interface compatibility — always null now (seek-based pagination
  // doesn't need callers to pass cursors back in).
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export interface PaginatedSuppliersResult {
  suppliers: Supplier[];
  totalCount: number;
  // Kept for interface compatibility — always null now.
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export interface SupplierStats {
  totalSuppliers: number;
  activeSuppliers: number;
  inactiveSuppliers: number;
}

// Generates a placeholder VIN for a brand-new unit whose real chassis number
// isn't known until the physical batch is unloaded and inspected. Staff
// replace this via the car's Edit form once the real VIN is known.
const pendingVin = (poId: string, n: number): string =>
  `PENDING-VIN-${poId.slice(0, 6).toUpperCase()}-${Date.now()}-${n}`;

export class ImportOrderService {
  static async generateImportOrderNumber(): Promise<string> {
    const year = new Date().getFullYear();
    const orders = await this.getImportOrders();
    const yearOrders = orders.filter(po => po.createdAt.getFullYear() === year);
    const nextNum = yearOrders.length + 1;
    return `IMP-${year}-${String(nextNum).padStart(5, '0')}`;
  }

  static async createImportOrder(
    formData: ImportOrderFormData,
    userId: string,
    userName: string
  ): Promise<string> {
    try {
      const supplier = await this.getSupplier(formData.supplierId);
      if (!supplier) throw new Error('Supplier not found');

      const poNumber = await this.generateImportOrderNumber();
      const subtotal = formData.items.reduce((sum, item) => sum + item.totalPrice, 0);
      const costs = formData.costs || [];
      const costsTotal = sumCosts(costs, subtotal);
      const totalAmount = subtotal + costsTotal;

      const initialStatusHistory: ImportOrderStatusHistoryEntry[] = [
        { status: ImportOrderStatus.DRAFT, changedBy: userId, changedByName: userName, changedAt: new Date() },
      ];

      const poData = {
        poNumber,
        supplierId: formData.supplierId,
        supplierName: supplier.name,
        supplierEmail: supplier.email,
        supplierPhone: supplier.phone,
        status: ImportOrderStatus.DRAFT,
        paymentStatus: PaymentStatus.UNPAID,
        items: formData.items.map(item => ({
          ...item,
          // A used/individual unit is sourced one vehicle at a time — its
          // Car record is created right away below, not deferred to a later
          // "receive" step the way a brand-new batch's units are.
          receivedQuantity: item.acquisitionType === 'used_individual' ? item.quantity : 0,
          warehouseId: item.warehouseId || null,
          warehouseName: item.warehouseName || null,
          carIds: [] as string[],
        })),
        subtotal,
        totalAmount,
        paidAmount: 0,
        expectedDeliveryDate: formData.expectedDeliveryDate || null,
        notes: formData.notes || '',
        internalNotes: formData.internalNotes || '',
        stockUpdated: false,
        costs,
        statusHistory: initialStatusHistory,
        payments: [] as ImportOrderPayment[],
        lcNumber: formData.lcNumber || '',
        lcStatus: formData.lcStatus || 'Not Opened',
        lcOpenedDate: formData.lcOpenedDate || null,
        documents: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        createdByName: userName,
      };

      const docRef = await addDoc(collection(db, IMPORT_ORDERS_COLLECTION), poData);
      const poId = docRef.id;

      // Immediately create a draft Car (status 'In Transit (Import)') for
      // every used/individual item — a used import shouldn't need a catalog
      // listing to already exist before it can be ordered (see answers.text).
      // Brand-new batch items get their Cars generated later, at receive
      // time (see markImportOrderAsReceived), once the batch has actually
      // arrived and can be allocated to a show room.
      const finalItems = [...poData.items];
      let anyUsedItem = false;
      for (let i = 0; i < finalItems.length; i++) {
        const item = finalItems[i];
        if (item.acquisitionType !== 'used_individual') continue;
        if (!item.warehouseId) throw new Error(`Please select a destination show room for the used vehicle item (${item.brandName} ${item.modelName})`);
        anyUsedItem = true;
        try {
          const carId = await ProductService.createProduct({
            name: `${item.modelYear ? item.modelYear + ' ' : ''}${item.brandName} ${item.modelName}${item.trimName ? ' ' + item.trimName : ''}`.trim(),
            description: '',
            price: String(item.unitPrice),
            discount: '0',
            unitCost: String(item.unitPrice),
            categoryId: item.categoryId,
            subcategoryId: '',
            brandId: item.brandId,
            modelId: item.modelId,
            trimId: item.trimId || '',
            sku: item.vin?.trim() || pendingVin(poId, i),
            images: [],
            branchId: item.warehouseId,
            modelYear: item.modelYear ? String(item.modelYear) : '',
            registrationYear: '',
            mileageKm: item.mileageKm ? String(item.mileageKm) : '',
            fuelType: '', transmission: '', engineCapacityCc: '',
            driveType: '', seatingCapacity: '',
            exteriorColor: item.exteriorColor || '',
            interiorColor: '',
            condition: item.condition || 'Used - Good',
            status: 'In Transit (Import)',
            registrationNumber: '', ownersCount: '',
            isImported: true, importOrderId: poId,
            countryOfOrigin: '',
            features: [],
            // Imported vehicles start hidden from the website — staff must
            // explicitly publish them once details/photos are filled in.
            isVisible: false,
          }, userId);
          finalItems[i] = { ...item, carIds: [carId] };
        } catch (err) {
          console.error(`Error creating draft car for used item ${item.brandName} ${item.modelName}:`, err);
          throw err;
        }
      }

      if (anyUsedItem) {
        await updateDoc(docRef, { items: finalItems });
      }

      return poId;
    } catch (error) {
      console.error('Error creating import order:', error);
      throw error;
    }
  }

  static async updateImportOrder(
    poId: string,
    formData: ImportOrderFormData
  ): Promise<void> {
    try {
      const supplier = await this.getSupplier(formData.supplierId);
      if (!supplier) throw new Error('Supplier not found');

      const subtotal = formData.items.reduce((sum, item) => sum + item.totalPrice, 0);
      const costs = formData.costs || [];
      const costsTotal = sumCosts(costs, subtotal);
      const totalAmount = subtotal + costsTotal;

      const updateData = {
        supplierId: formData.supplierId,
        supplierName: supplier.name,
        supplierEmail: supplier.email,
        supplierPhone: supplier.phone,
        items: formData.items,
        subtotal,
        totalAmount,
        expectedDeliveryDate: formData.expectedDeliveryDate || null,
        notes: formData.notes || '',
        internalNotes: formData.internalNotes || '',
        costs,
        lcNumber: formData.lcNumber || '',
        lcStatus: formData.lcStatus || 'Not Opened',
        lcOpenedDate: formData.lcOpenedDate || null,
        updatedAt: serverTimestamp(),
      };

      const poRef = doc(db, IMPORT_ORDERS_COLLECTION, poId);
      await updateDoc(poRef, updateData);
    } catch (error) {
      console.error('Error updating import order:', error);
      throw error;
    }
  }

  static async updateImportOrderStatus(
    poId: string,
    status: ImportOrderStatus,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      const poRef = doc(db, IMPORT_ORDERS_COLLECTION, poId);
      const poSnap = await getDoc(poRef);
      const existingHistory: ImportOrderStatusHistoryEntry[] = poSnap.exists() ? (poSnap.data().statusHistory || []) : [];

      const historyEntry: ImportOrderStatusHistoryEntry = {
        status, changedBy: userId, changedByName: userName, changedAt: new Date(),
      };

      const updateData: any = {
        status,
        statusHistory: [...existingHistory, historyEntry],
        updatedAt: serverTimestamp(),
      };

      if (status === ImportOrderStatus.APPROVED) {
        updateData.approvedBy = userId;
        updateData.approvedByName = userName;
        updateData.approvedAt = serverTimestamp();
      }

      await updateDoc(poRef, updateData);
    } catch (error) {
      console.error('Error updating import order status:', error);
      throw error;
    }
  }

  // Marks an Import Order as received and generates the real Car records for
  // it — replacing the old behaviour of just incrementing a generic
  // warehouse quantity counter (see answers.text). `itemsWithWarehouses` is
  // built by ImportOrderViewDialog: brand-new batch items are expanded one
  // entry per show-room split (each with its own quantity/warehouseId);
  // used/individual items pass through as-is, already carrying the single
  // Car id created for them at order time (see createImportOrder above).
  static async markImportOrderAsReceived(
    poId: string,
    itemsWithWarehouses: ImportOrderItem[],
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      const missingWarehouse = itemsWithWarehouses.find(item => !item.warehouseId);
      if (missingWarehouse) throw new Error('All items must have a show room assigned');

      await runTransaction(db, async (transaction) => {
        const poRef = doc(db, IMPORT_ORDERS_COLLECTION, poId);
        const poDoc = await transaction.get(poRef);

        if (!poDoc.exists()) throw new Error('Import order not found');

        const poData = poDoc.data();
        if (poData.stockUpdated) throw new Error('This import order has already been received');

        const existingHistory: ImportOrderStatusHistoryEntry[] = poData.statusHistory || [];
        const historyEntry: ImportOrderStatusHistoryEntry = {
          status: ImportOrderStatus.RECEIVED, changedBy: userId, changedByName: userName, changedAt: new Date(),
        };

        transaction.update(poRef, {
          status: ImportOrderStatus.RECEIVED,
          statusHistory: [...existingHistory, historyEntry],
          items: itemsWithWarehouses.map(item => ({
            ...item,
            receivedQuantity: item.quantity,
          })),
          receivedBy: userId,
          receivedByName: userName,
          receivedAt: serverTimestamp(),
          actualDeliveryDate: serverTimestamp(),
          stockUpdated: true,
          updatedAt: serverTimestamp(),
        });
      });

      const finalItems: ImportOrderItem[] = [];
      for (const item of itemsWithWarehouses) {
        if (item.acquisitionType === 'used_individual') {
          // Already has its Car record — just confirm arrival.
          const carId = item.carIds?.[0];
          if (carId) {
            try {
              await ProductService.markAsInStock(carId);
            } catch (err) {
              console.error(`Error marking car ${carId} as in stock:`, err);
            }
          }
          finalItems.push(item);
          continue;
        }

        // Brand-new batch: generate one real Car record per unit in this
        // show-room split — this is the fix for the old fake "quantity"
        // bump described in answers.text.
        const generatedIds: string[] = [];
        for (let n = 0; n < item.quantity; n++) {
          try {
            const carId = await ProductService.createProduct({
              name: `${item.brandName} ${item.modelName}${item.trimName ? ' ' + item.trimName : ''}`.trim(),
              description: '',
              price: String(item.unitPrice),
              discount: '0',
              unitCost: String(item.unitPrice),
              categoryId: item.categoryId,
              subcategoryId: '',
              brandId: item.brandId,
              modelId: item.modelId,
              trimId: item.trimId || '',
              sku: pendingVin(poId, n),
              images: [],
              branchId: item.warehouseId!,
              modelYear: '', registrationYear: '', mileageKm: '',
              fuelType: '', transmission: '', engineCapacityCc: '',
              driveType: '', seatingCapacity: '',
              exteriorColor: '', interiorColor: '',
              condition: 'Brand New',
              status: 'In Stock',
              registrationNumber: '', ownersCount: '',
              isImported: true, importOrderId: poId,
              countryOfOrigin: '',
              features: [],
              // Imported vehicles start hidden from the website — staff must
              // explicitly publish them once details/photos are filled in.
              isVisible: false,
            }, userId);
            generatedIds.push(carId);
          } catch (err) {
            console.error(`Error creating car for item ${item.brandName} ${item.modelName}:`, err);
          }
        }
        finalItems.push({ ...item, carIds: [...(item.carIds || []), ...generatedIds] });
      }

      await updateDoc(doc(db, IMPORT_ORDERS_COLLECTION, poId), { items: finalItems });
    } catch (error) {
      console.error('Error marking import order as received:', error);
      throw error;
    }
  }

  // Records one payment against the order — appends a dated, attributed
  // entry to `payments` (rather than just overwriting a running total) so
  // the payment history/dates are preserved for display.
  static async recordPayment(
    poId: string,
    amount: number,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      const po = await this.getImportOrder(poId);
      if (!po) throw new Error('Import order not found');

      const paidAmount = po.paidAmount + amount;
      let paymentStatus: PaymentStatus;
      if (paidAmount === 0) paymentStatus = PaymentStatus.UNPAID;
      else if (paidAmount >= po.totalAmount) paymentStatus = PaymentStatus.PAID;
      else paymentStatus = PaymentStatus.PARTIAL;

      const paymentEntry: ImportOrderPayment = {
        id: `pay-${Date.now()}`, amount, paidAt: new Date(), recordedBy: userId, recordedByName: userName,
      };

      const poRef = doc(db, IMPORT_ORDERS_COLLECTION, poId);
      await updateDoc(poRef, {
        paidAmount,
        paymentStatus,
        payments: [...(po.payments || []), paymentEntry],
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error recording payment:', error);
      throw error;
    }
  }

  static async updateReceivedQuantities(
    poId: string,
    itemUpdates: { id: string; receivedQuantity: number }[]
  ): Promise<void> {
    try {
      const po = await this.getImportOrder(poId);
      if (!po) throw new Error('Import order not found');

      const updatedItems = po.items.map(item => {
        const update = itemUpdates.find(u => u.id === item.id);
        return update ? { ...item, receivedQuantity: update.receivedQuantity } : item;
      });

      const poRef = doc(db, IMPORT_ORDERS_COLLECTION, poId);
      await updateDoc(poRef, { items: updatedItems, updatedAt: serverTimestamp() });
    } catch (error) {
      console.error('Error updating received quantities:', error);
      throw error;
    }
  }

  // Uploads import-related paperwork (LC copy, invoice, bill of lading,
  // customs declaration, etc.) and merges them into the order's existing
  // `documents` array. Mirrors importOrderReturnService.uploadReturnImages —
  // same storage-path convention, same Promise.all pattern. Available for the
  // order's entire lifecycle (not gated to DRAFT), since paperwork like a
  // Bill of Lading typically only exists once the order has shipped.
  static async uploadImportOrderDocuments(
    poId: string,
    files: { file: File; type: ImportOrderDocumentType }[]
  ): Promise<ImportOrderDocument[]> {
    try {
      const uploaded: ImportOrderDocument[] = await Promise.all(
        files.map(async ({ file, type }, index) => {
          const path = `importOrders/${poId}/documents/${Date.now()}_${index}_${file.name}`;
          const fileRef = ref(storage, path);
          await uploadBytes(fileRef, file);
          const url = await getDownloadURL(fileRef);
          return {
            id: `doc-${Date.now()}-${index}`,
            name: file.name,
            type,
            url,
            uploadedAt: new Date(),
          };
        })
      );

      const po = await this.getImportOrder(poId);
      const merged = [...(po?.documents || []), ...uploaded];
      await updateDoc(doc(db, IMPORT_ORDERS_COLLECTION, poId), {
        documents: merged,
        updatedAt: serverTimestamp(),
      });

      return uploaded;
    } catch (error) {
      console.error('Error uploading import order documents:', error);
      throw error;
    }
  }

  // Updates Letter of Credit tracking fields — independent of the order's
  // status workflow, since LC status typically changes after the order has
  // left DRAFT (when the full edit form is no longer available).
  static async updateLcDetails(
    poId: string,
    lcNumber: string,
    lcStatus: LcStatus,
    lcOpenedDate: Date | null
  ): Promise<void> {
    try {
      const poRef = doc(db, IMPORT_ORDERS_COLLECTION, poId);
      await updateDoc(poRef, {
        lcNumber,
        lcStatus,
        lcOpenedDate: lcOpenedDate || null,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating LC details:', error);
      throw error;
    }
  }

  /* ===================== GET IMPORT ORDERS PAGINATED =====================
   *
   * FIX: Replaced cursor-based pagination (startAfter / endBefore /
   * limitToLast) with a two-step cursor-seek so any page can be fetched
   * in any order (page 1 → page 5 → page 2 all work correctly).
   *
   * HOW IT WORKS
   * ─────────────
   * Firestore's client SDK has no offset(). The classic startAfter cursor
   * only works sequentially. To jump to page N we:
   *
   *   Step 1 — fetch only the first (page-1)*pageSize doc IDs (lightweight
   *             seek). The last doc from this set becomes our startAfter cursor.
   *   Step 2 — fetch the real pageSize docs starting after that cursor.
   *
   * For page 1 the seek is skipped entirely (skip count = 0).
   * ========================================================= */
  static async getImportOrdersPaginated(
    pageSize: number = 10,
    page: number = 1,
    searchQuery?: string,
    statusFilter?: ImportOrderStatus,
    paymentFilter?: PaymentStatus
  ): Promise<PaginatedImportOrdersResult> {
    try {
      const orders: ImportOrder[] = [];
      const collectionRef = collection(db, IMPORT_ORDERS_COLLECTION);

      // ── Build filter constraints (where clauses only, no ordering/paging) ──
      const filterConstraints: any[] = [];
      if (statusFilter) filterConstraints.push(where('status', '==', statusFilter));
      if (paymentFilter) filterConstraints.push(where('paymentStatus', '==', paymentFilter));

      const orderConstraint = orderBy('createdAt', 'desc');

      // ── Total count (0 doc reads) ──────────────────────────────────────────
      const countSnapshot = await getCountFromServer(
        query(collectionRef, ...filterConstraints)
      );
      const totalCount = countSnapshot.data().count;

      // ── Step 1: cursor seek ────────────────────────────────────────────────
      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, ...filterConstraints, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      // ── Step 2: fetch the real page ────────────────────────────────────────
      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, ...filterConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, ...filterConstraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      // ── Map + client-side search filter ───────────────────────────────────
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        const order: ImportOrder = {
          id: docSnap.id,
          poNumber: data.poNumber,
          supplierId: data.supplierId,
          supplierName: data.supplierName,
          supplierEmail: data.supplierEmail,
          supplierPhone: data.supplierPhone,
          status: data.status,
          paymentStatus: data.paymentStatus,
          items: data.items || [],
          subtotal: data.subtotal,
          taxRate: data.taxRate,
          taxAmount: data.taxAmount,
          shippingCost: data.shippingCost,
          totalAmount: data.totalAmount,
          paidAmount: data.paidAmount || 0,
          expectedDeliveryDate: data.expectedDeliveryDate?.toDate() || undefined,
          actualDeliveryDate: data.actualDeliveryDate?.toDate() || undefined,
          notes: data.notes || '',
          internalNotes: data.internalNotes || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          createdBy: data.createdBy,
          createdByName: data.createdByName,
          approvedBy: data.approvedBy,
          approvedByName: data.approvedByName,
          approvedAt: data.approvedAt?.toDate() || undefined,
          receivedBy: data.receivedBy,
          receivedByName: data.receivedByName,
          receivedAt: data.receivedAt?.toDate() || undefined,
          stockUpdated: data.stockUpdated || false,
          returnIds: data.returnIds || [],
          costs: data.costs || [],
          cifValue: data.cifValue || 0,
          customsDuty: data.customsDuty || 0,
          vatAmount: data.vatAmount || 0,
          luxuryTax: data.luxuryTax || 0,
          otherTaxes: data.otherTaxes || [],
          statusHistory: (data.statusHistory || []).map((h: any) => ({
            ...h,
            changedAt: h.changedAt?.toDate ? h.changedAt.toDate() : h.changedAt,
          })),
          payments: (data.payments || []).map((p: any) => ({
            ...p,
            paidAt: p.paidAt?.toDate ? p.paidAt.toDate() : p.paidAt,
          })),
          lcNumber: data.lcNumber || '',
          lcStatus: data.lcStatus || 'Not Opened',
          lcOpenedDate: data.lcOpenedDate?.toDate() || undefined,
          documents: (data.documents || []).map((d: any) => ({
            ...d,
            uploadedAt: d.uploadedAt?.toDate ? d.uploadedAt.toDate() : d.uploadedAt,
          })),
        };

        if (searchQuery && searchQuery.trim() !== '') {
          const searchLower = searchQuery.toLowerCase();
          const matchesSearch =
            order.poNumber.toLowerCase().includes(searchLower) ||
            order.supplierName.toLowerCase().includes(searchLower) ||
            order.supplierEmail.toLowerCase().includes(searchLower) ||
            (order.supplierPhone && order.supplierPhone.toLowerCase().includes(searchLower)) ||
            (order.notes && order.notes.toLowerCase().includes(searchLower)) ||
            (order.createdByName && order.createdByName.toLowerCase().includes(searchLower));
          if (matchesSearch) orders.push(order);
        } else {
          orders.push(order);
        }
      });

      return {
        orders,
        totalCount,
        lastVisible: null,   // no longer used by callers
        firstVisible: null,  // no longer used by callers
      };
    } catch (error) {
      console.error('Error fetching paginated import orders:', error);
      throw error;
    }
  }

  /**
   * @deprecated Use getImportOrdersPaginated instead
   */
  static async getImportOrders(): Promise<ImportOrder[]> {
    try {
      const q = query(collection(db, IMPORT_ORDERS_COLLECTION), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          expectedDeliveryDate: data.expectedDeliveryDate?.toDate() || undefined,
          actualDeliveryDate: data.actualDeliveryDate?.toDate() || undefined,
          approvedAt: data.approvedAt?.toDate() || undefined,
          receivedAt: data.receivedAt?.toDate() || undefined,
          stockUpdated: data.stockUpdated || false,
          lcOpenedDate: data.lcOpenedDate?.toDate() || undefined,
          statusHistory: (data.statusHistory || []).map((h: any) => ({
            ...h,
            changedAt: h.changedAt?.toDate ? h.changedAt.toDate() : h.changedAt,
          })),
          payments: (data.payments || []).map((p: any) => ({
            ...p,
            paidAt: p.paidAt?.toDate ? p.paidAt.toDate() : p.paidAt,
          })),
          documents: (data.documents || []).map((d: any) => ({
            ...d,
            uploadedAt: d.uploadedAt?.toDate ? d.uploadedAt.toDate() : d.uploadedAt,
          })),
        } as ImportOrder;
      });
    } catch (error) {
      console.error('Error fetching import orders:', error);
      throw error;
    }
  }

  static async getImportOrder(poId: string): Promise<ImportOrder | null> {
    try {
      const docRef = doc(db, IMPORT_ORDERS_COLLECTION, poId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          expectedDeliveryDate: data.expectedDeliveryDate?.toDate() || undefined,
          actualDeliveryDate: data.actualDeliveryDate?.toDate() || undefined,
          approvedAt: data.approvedAt?.toDate() || undefined,
          receivedAt: data.receivedAt?.toDate() || undefined,
          stockUpdated: data.stockUpdated || false,
          lcOpenedDate: data.lcOpenedDate?.toDate() || undefined,
          statusHistory: (data.statusHistory || []).map((h: any) => ({
            ...h,
            changedAt: h.changedAt?.toDate ? h.changedAt.toDate() : h.changedAt,
          })),
          payments: (data.payments || []).map((p: any) => ({
            ...p,
            paidAt: p.paidAt?.toDate ? p.paidAt.toDate() : p.paidAt,
          })),
          documents: (data.documents || []).map((d: any) => ({
            ...d,
            uploadedAt: d.uploadedAt?.toDate ? d.uploadedAt.toDate() : d.uploadedAt,
          })),
        } as ImportOrder;
      }
      return null;
    } catch (error) {
      console.error('Error fetching import order:', error);
      throw error;
    }
  }

  static async deleteImportOrder(poId: string): Promise<void> {
    try {
      const po = await this.getImportOrder(poId);
      if (po && po.stockUpdated) {
        throw new Error('Cannot delete an import order that has already generated cars. Please handle those vehicles first.');
      }
      const poRef = doc(db, IMPORT_ORDERS_COLLECTION, poId);
      await deleteDoc(poRef);
    } catch (error) {
      console.error('Error deleting import order:', error);
      throw error;
    }
  }

  /* ===================== GET IMPORT ORDER STATS =====================
   * Uses getCountFromServer() for count-only fields (no document reads).
   * Financial aggregates (totalValue, totalPaid, totalOutstanding) still
   * require reading documents — they are fetched once and aggregated here.
   * ========================================================= */
  static async getImportOrderStats(): Promise<ImportOrderStats> {
    try {
      const col = collection(db, IMPORT_ORDERS_COLLECTION);

      const [
        totalSnap,
        draftSnap,
        pendingSnap,
        approvedSnap,
        orderedSnap,
        receivedSnap,
        cancelledSnap,
        docsSnap,
      ] = await Promise.all([
        getCountFromServer(query(col)),
        getCountFromServer(query(col, where('status', '==', ImportOrderStatus.DRAFT))),
        getCountFromServer(query(col, where('status', '==', ImportOrderStatus.PENDING))),
        getCountFromServer(query(col, where('status', '==', ImportOrderStatus.APPROVED))),
        getCountFromServer(query(col, where('status', '==', ImportOrderStatus.ORDERED))),
        getCountFromServer(query(col, where('status', '==', ImportOrderStatus.RECEIVED))),
        getCountFromServer(query(col, where('status', '==', ImportOrderStatus.CANCELLED))),
        getDocs(query(col, orderBy('createdAt', 'desc'))),
      ]);

      let totalValue = 0;
      let totalPaid = 0;
      docsSnap.forEach(d => {
        totalValue += d.data().totalAmount || 0;
        totalPaid += d.data().paidAmount || 0;
      });

      return {
        total: totalSnap.data().count,
        draft: draftSnap.data().count,
        pending: pendingSnap.data().count,
        approved: approvedSnap.data().count,
        ordered: orderedSnap.data().count,
        received: receivedSnap.data().count,
        cancelled: cancelledSnap.data().count,
        totalValue,
        totalPaid,
        totalOutstanding: totalValue - totalPaid,
      };
    } catch (error) {
      console.error('Error getting import order stats:', error);
      throw error;
    }
  }

  static async getSupplierStats(): Promise<SupplierStats> {
    try {
      const collectionRef = collection(db, SUPPLIER_COLLECTION);
      const [totalSnapshot, activeSnapshot] = await Promise.all([
        getCountFromServer(collectionRef),
        getCountFromServer(query(collectionRef, where('isActive', '==', true))),
      ]);
      const totalSuppliers = totalSnapshot.data().count;
      const activeSuppliers = activeSnapshot.data().count;
      return { totalSuppliers, activeSuppliers, inactiveSuppliers: totalSuppliers - activeSuppliers };
    } catch (error) {
      console.error('Error fetching supplier stats:', error);
      throw error;
    }
  }

  /* ===================== GET SUPPLIERS PAGINATED =====================
   *
   * FIX: Same seek-based pagination pattern as getImportOrdersPaginated.
   * ================================================================== */
  static async getSuppliersPaginated(
    pageSize: number = 10,
    page: number = 1,
    searchQuery?: string,
    statusFilter?: boolean
  ): Promise<PaginatedSuppliersResult> {
    try {
      const suppliers: Supplier[] = [];
      const collectionRef = collection(db, SUPPLIER_COLLECTION);

      // ── Build filter constraints ───────────────────────────────────────────
      const filterConstraints: any[] = [];
      if (statusFilter !== undefined) filterConstraints.push(where('isActive', '==', statusFilter));

      const orderConstraint = orderBy('createdAt', 'desc');

      // ── Total count (0 doc reads) ──────────────────────────────────────────
      const countSnapshot = await getCountFromServer(
        query(collectionRef, ...filterConstraints)
      );
      const totalCount = countSnapshot.data().count;

      // ── Step 1: cursor seek ────────────────────────────────────────────────
      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, ...filterConstraints, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      // ── Step 2: fetch the real page ────────────────────────────────────────
      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, ...filterConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, ...filterConstraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      // ── Map + client-side search filter ───────────────────────────────────
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        const supplier: Supplier = {
          id: docSnap.id,
          name: data.name,
          email: data.email,
          phone: data.phone,
          address: data.address || '',
          contactPerson: data.contactPerson || '',
          taxId: data.taxId || '',
          paymentTerms: data.paymentTerms || '',
          isActive: data.isActive !== false,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        };

        if (searchQuery && searchQuery.trim() !== '') {
          const searchLower = searchQuery.toLowerCase();
          const matchesSearch =
            supplier.name.toLowerCase().includes(searchLower) ||
            supplier.email.toLowerCase().includes(searchLower) ||
            (supplier.phone && supplier.phone.toLowerCase().includes(searchLower)) ||
            (supplier.contactPerson && supplier.contactPerson.toLowerCase().includes(searchLower)) ||
            (supplier.taxId && supplier.taxId.toLowerCase().includes(searchLower)) ||
            (supplier.address && supplier.address.toLowerCase().includes(searchLower));
          if (matchesSearch) suppliers.push(supplier);
        } else {
          suppliers.push(supplier);
        }
      });

      return {
        suppliers,
        totalCount,
        lastVisible: null,   // no longer used by callers
        firstVisible: null,  // no longer used by callers
      };
    } catch (error) {
      console.error('Error fetching paginated suppliers:', error);
      throw error;
    }
  }

  static async createSupplier(
    supplier: Omit<Supplier, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, SUPPLIER_COLLECTION), {
        ...supplier,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating supplier:', error);
      throw error;
    }
  }

  static async getSupplier(supplierId: string): Promise<Supplier | null> {
    try {
      const docRef = doc(db, SUPPLIER_COLLECTION, supplierId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as Supplier;
      }
      return null;
    } catch (error) {
      console.error('Error fetching supplier:', error);
      throw error;
    }
  }

  static async updateSupplier(
    supplierId: string,
    supplierData: Omit<Supplier, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<void> {
    try {
      const supplierRef = doc(db, SUPPLIER_COLLECTION, supplierId);
      await updateDoc(supplierRef, { ...supplierData, updatedAt: serverTimestamp() });
    } catch (error) {
      console.error('Error updating supplier:', error);
      throw error;
    }
  }

  static async deleteSupplier(supplierId: string): Promise<void> {
    try {
      const supplierRef = doc(db, SUPPLIER_COLLECTION, supplierId);
      await deleteDoc(supplierRef);
    } catch (error) {
      console.error('Error deleting supplier:', error);
      throw error;
    }
  }

  /**
   * @deprecated Use getSuppliersPaginated instead
   */
  static async getSuppliers(): Promise<Supplier[]> {
    try {
      const q = query(
        collection(db, SUPPLIER_COLLECTION),
        where('isActive', '==', true),
        orderBy('name', 'asc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      } as Supplier));
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      try {
        const q = query(collection(db, SUPPLIER_COLLECTION), where('isActive', '==', true));
        const snapshot = await getDocs(q);
        const suppliers = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
        } as Supplier));
        return suppliers.sort((a, b) => a.name.localeCompare(b.name));
      } catch (fallbackError) {
        console.error('Error in fallback query:', fallbackError);
        throw fallbackError;
      }
    }
  }

  /**
   * @deprecated Use getSuppliersPaginated instead
   */
  static async getAllSuppliers(): Promise<Supplier[]> {
    try {
      const q = query(collection(db, SUPPLIER_COLLECTION), orderBy('name', 'asc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      } as Supplier));
    } catch (error) {
      console.error('Error fetching all suppliers:', error);
      try {
        const snapshot = await getDocs(collection(db, SUPPLIER_COLLECTION));
        const suppliers = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
        } as Supplier));
        return suppliers.sort((a, b) => a.name.localeCompare(b.name));
      } catch (fallbackError) {
        console.error('Error in fallback query:', fallbackError);
        throw fallbackError;
      }
    }
  }
}
